import {L} from "../lt-ng/landcare-toolkit";
import {LTServices} from "../lt-ng/lt-services";
import IMapService = LTServices.IMapService;
import Item = L.tree.Item;
import getAllCheckedNodes = L.tree.getAllCheckedNodes;
import {IToastService} from "./ToastService";
import filterNodes = L.tree.filterNodes;
import getParents = L.tree.getParents;
import {ICoordinateAdjustmentService} from "./coordinate-adjustment";
import nodeIsScienceLayer = L.tree.nodeIsScienceLayer;
import {_F} from "../utility/func";
import combineFeatures = _F.combineFeatures;
import {GeoAppEvents} from "../controllers/events/geoapp_events";
import {Geometry} from "ol/geom";
import {GeoJSON, WKT} from "ol/format";
import {Feature} from "ol";
import {Coordinate} from "ol/coordinate";

export type MapShareGeometryFormat = 'WKT' | 'GeoJSON';

export interface IMapShareData {
	id?: number;
	hash?: string;
	x: string;
	y: string;
	zoom: number;
	layers: string;
	includedLayers?: string;
	creator?: number;
	createdAt?: string;
	lastEdited?: string;
	geometryWKT?: string;
	geometryFormat?: MapShareGeometryFormat;
	legend?: string;
}

export interface IMapShare extends ng.resource.IResource<IMapShareData> {
	id?: number;
	hash?: string;
	x: string;
	y: string;
	zoom: number;
	layers: string;
	includedLayers?: string;
	creator?: number;
	createdAt?: string;
	lastEdited?: string;
	geometryWKT?: string;
    geometryFormat?: MapShareGeometryFormat;
	legend?: string;
}

export interface IMapShareResource extends ng.resource.IResourceClass<IMapShare> {

}

export interface IMapShareService {
	createSharePayload(center?: [number, number], legendString?: string) : IMapShareData;
	createShare(legendString: string) : ng.IPromise<IMapShare | IMapShareData>;
	getShare(hash: string) : ng.IPromise<IMapShare | IMapShareData>;
	setActiveShare(share : IMapShare) : void;
	getActiveShare() : IMapShare;
	zoomTo(state: IMapShareData, skipLayers?: boolean|undefined): ng.IPromise<void>;

	createAreaReportSharePayload(geometry : Geometry, legendString?: string) : IMapShareData;
    createFeaturesSharePaylod(features: Feature<Geometry>[], legendString?: string, properties?: string[]) : IMapShareData;
	createAreaReportShare(payload : IMapShareData) : ng.IPromise<IMapShare | IMapShareData>;

}

export class MapShareService implements IMapShareService{
	public static $inject = [
		'$resource',
		'landcareMapService',
		'$q',
		'$rootScope',
		'toastService',
		'coordinateAdjustmentService'
	];
	static readonly PARAM = 'm';
	constructor(protected $resource : ng.resource.IResourceService, protected mapService : IMapService,
				protected $q : ng.IQService, protected $rootScope : ng.IRootScopeService,
				// protected areaQueryService : IAreaQueryService<any>,
				protected toastService: IToastService,
				protected coordinateAdjustmentService: ICoordinateAdjustmentService) {
		const params = new URLSearchParams(window.location.search);
		const paramValue = params.get(MapShareService.PARAM);
		if (paramValue) {
			this.getShare(paramValue)
				.then((mapShareData: IMapShareData) => {
					this.zoomTo(mapShareData);
				});
		}
	}

	public zoomTo(mapShareData : IMapShareData, skipLayers = false) {
		if (!mapShareData || !mapShareData.x || !mapShareData.y || mapShareData.zoom === undefined) return this.$q.resolve();
		console.log('ZoomTo');
		console.log(mapShareData);
		return this.mapService.mapPromise.then((map) => {
			// console.log("Displaying map share...", mapShareData);
			let v = map.getView();
			let centre : Coordinate = this.coordinateAdjustmentService.adjustCentreForScreenSpace(
				[Number(mapShareData.x), Number(mapShareData.y)],
					map,
					mapShareData.zoom
			);

			v.setCenter(centre);
			v.setZoom(mapShareData.zoom);

			if (!skipLayers) {
				this.mapService.promise.then(() => {
					let update = () => {
						this.setupLayerTree(mapShareData);

						this.$rootScope.$broadcast(GeoAppEvents.displayShare, mapShareData);
					};
					if (this.$rootScope.$$phase) update(); else this.$rootScope.$apply(update);
				});
			} else {
				this.$rootScope.$broadcast(GeoAppEvents.displayShare, mapShareData);
			}
		});
	}

	protected setupLayerTree(mapShareData : IMapShareData) {
		if (!mapShareData.layers) return;
		const treeData = this.mapService.treeData;
		let processLayerString = (layerStr) => layerStr.split(',').map((item) => { return item.split('=')});
		let layers = processLayerString(mapShareData.layers);
		let layerIds = {};
		let classIds = {};
		layers.forEach((layerItem : string[]) => {
			if (layerItem.length == 2) {
				let lType = layerItem[0];
				let id = layerItem[1];
				if (lType == 'l') layerIds[id] = true;
				else if (lType == 'c') classIds[id] = true;
			}
		});

		if (mapShareData.includedLayers) {
			let includedLayerIds = {};
			let includedClassIds = {};

			let included = processLayerString(mapShareData.includedLayers);
			included.forEach((layerItem : string[]) => {
				if (layerItem.length == 2) {
					let lType = layerItem[0];
					let id = layerItem[1];
					if (lType == 'l') includedLayerIds[id] = true;
					else if (lType == 'c') includedClassIds[id] = true;
				}
			});
			// Exclude every layer... for now.
			filterNodes(treeData, (n) => {
				return !n.isCustom
					&& ['CONTEXT', 'BASE'].indexOf(n.layerType) == -1
					&& !(n.children && n.children.some((c) => c.isCustom))
			}).forEach((n) => {
				n.extraState.metaLayerSelector.checked = false;
			});

			let includeNodeAndParents = (n : Item) => {
				n.extraState.metaLayerSelector.checked = true;
				L.tree.getParents(n).forEach((p) => {
					p.extraState.metaLayerSelector.checked = true;
				})
			};

			let includeChildren = (n : Item) => {
				(n.children || []).forEach((n) => {
					n.extraState.metaLayerSelector.checked = true;
					(n.children || []).forEach(includeChildren);
				});
			};

			// Include map-share's classes
			Object.keys(includedClassIds)
				.forEach((id) => {
					L.tree.getClassById(treeData, Number(id))
						.forEach(includeNodeAndParents);
				});

			// Include layers which are alternatives to the map-share's included classes
			let includedByAltClass = filterNodes(treeData, (n) => {
					let altClasses = (n.layerService && n.layerService.alternativeClasses) || [];
					return altClasses && n.layerId && altClasses.some((c) => includedClassIds[c]);
				});
			includedByAltClass.forEach(includeNodeAndParents);
			includedByAltClass.forEach(includeChildren);

			// Include layers which are alternatives to those in the map-share
			let includedByAltLayer = filterNodes(treeData, (n) => {
					let altLayers = (n.layerService && n.layerService.alternatives) || [];
					return altLayers.some((l) => includedLayerIds[l]);
				});

			includedByAltLayer.forEach(includeNodeAndParents);
			includedByAltLayer.forEach(includeChildren);

			// Include the map-share's layers
			Object.keys(includedLayerIds)
				.forEach((id) => {
					let ls = L.tree.getLayerById(treeData, Number(id));
					ls.forEach(includeNodeAndParents);
					ls.forEach(includeChildren);
				});
		}

		for(let item of this.mapService.treeData) {
			L.tree.uncheckChildren(item, true);
		}

		let checkItems = (item : Item) => {
			let ls = item.layerService;
			let alts = ls && ls.alternatives || [];
			let altClasses = ls && ls.alternativeClasses || [];
			// checked class, checked layer or an 'alternative' to this layer is checked
			let checked = !!((item.classId && classIds[item.classId])
				|| (item.layerId && layerIds[item.layerId])
				|| (alts.some((layerId) => layerIds[layerId]))
				|| (item.layerId && altClasses.some((classId) => classIds[classId]))
				|| false)

			item.checked = checked;
			item.layer && item.layer.setVisible(checked);
			if (item.children && item.children.length) {
				let allChecked = true;
				let anyChecked = false;
				if (checked) {
					for(let child of item.children) {
						child.checked = true;
						child.layer && child.layer.setVisible(true);
					}
				} else {
					for(let child of item.children) {
						let childIsChecked =  checkItems(child);
						allChecked = allChecked && childIsChecked;
						anyChecked = anyChecked || childIsChecked;
					}
					if (item.layerType == 'SCI_GROUP') {
						item.expanded = anyChecked;
						item.checked = anyChecked;
						/* TODO - the expanded property doesn't do anything.
						 *  Will need to look at how tree control handles this
						 */
					}

					if (anyChecked && !item.layerType) {
						checked = true;
						item.checked = true;
					}
				}
			}
			if (item.checked) {
				let ps = getParents(item);
				ps.forEach((parent) => parent.checked = true);

				if (nodeIsScienceLayer(item)) {
					ps.forEach((p) => p.expanded = true);
				}
			}
			return checked;
		};

		treeData.forEach(checkItems);

		this.$rootScope.$broadcast(GeoAppEvents.treeChange, true);
	}

	protected url = "/api/MapShare/:ID";

	protected getMapShareResource() : IMapShareResource {
		return <IMapShareResource> this.$resource(this.url, {
			'ID' : '@ID'
		})
	}

	protected readMapShareResource() : IMapShareResource {
		return <IMapShareResource> this.$resource("/MapShare/byhash/:Hash", {
			'Hash' : '@Hash'
		})
	}

	protected _activeShare : IMapShare = null;

	setActiveShare(share : IMapShare) {
		let oldValue = this._activeShare;
		this._activeShare = share;
		this.$rootScope.$broadcast('mapShareChange', share, oldValue);
	}

	getActiveShare() {
		return this._activeShare;
	}

	createAreaReportSharePayload(geometry : Geometry, legendString: string) : IMapShareData {
		let format = new WKT();
		let extent = geometry.getExtent();
		let x = (extent[0] + extent[2]) / 2;
		let y =  (extent[1] + extent[3]) / 2;
		let payload = <IMapShareData> this.createSharePayload([x,y], legendString || '');
		payload.geometryFormat = 'WKT';
		payload.geometryWKT = format.writeGeometry(geometry);
		return payload;
	}

    createFeaturesSharePaylod(features: Feature<Geometry>[], legendString: string = '', properties: string[] = []): IMapShareData {
        const format = new GeoJSON();

        if (properties.length) {
			features = features.map(f => {
				const clone = new Feature(f.getGeometry())

				properties.forEach(p => {
					clone.set(p, f.get(p));
				});

				return clone;
			});
		}

        let geometry = combineFeatures(features);
        let extent = geometry.getExtent();
        let x = (extent[0] + extent[2]) / 2;
        let y =  (extent[1] + extent[3]) / 2;
        let payload = <IMapShareData> this.createSharePayload([x,y], legendString || '');
        payload.geometryFormat = 'GeoJSON';
        payload.geometryWKT = format.writeFeatures(features);
        return payload;
    }



	createAreaReportShare(payload : IMapShareData) : ng.IPromise<IMapShare | IMapShareData> {
		return (new (this.getMapShareResource())(payload)).$save();
	}

	createSharePayload(centre : Coordinate = null, legendString: string = '') : IMapShareData {
		let checkedNodes = [];
		let map = this.mapService.map;
		let treeData = this.mapService.treeData;
		if (!map || !treeData) return null;
		for (let child of treeData) {
			checkedNodes = checkedNodes.concat(getAllCheckedNodes(<Item> child));
		}

		let layers = checkedNodes
			.filter((item : Item) => item.layerId || item.classId)
			// Don't share custom layers
			.filter((item: Item) => !item.isCustom)
			.map((item : Item) => {
				if (item.layerId) {
					return 'l=' + item.layerId;
				}
				return 'c=' + item.classId;
			}).join(',');

		let includedLayers = filterNodes(treeData, (n) => n.extraState.metaLayerSelector.checked)
			.filter((item : Item) => item.layerId || item.classId)
			// Don't share custom layers
			.filter((item: Item) => !item.isCustom)
			.map((item : Item) => {
				if (item.layerId) {
					return 'l=' + item.layerId;
				}
				return 'c=' + item.classId;
			}).join(',');
		// console.log(checkedNodes);
		let zoom = Math.round(map.getView().getZoom()); // was getting Zoom = 3.04 which causes an error in the API

		if (!centre) {
			try {
				centre = this.coordinateAdjustmentService.getCentreAdjustedForScreenSpace(map);
				console.log('MapShareAPI', centre, map.getView().getCenter(), this.coordinateAdjustmentService.adjustCentreForScreenSpace(centre, map, map.getView().getZoom()));
			} catch (e) {
				centre = map.getView().getCenter();
			}
		}

		try {
			return {
				x: centre[0].toString(),
				y: centre[1].toString(),
				zoom,
				layers,
				includedLayers,
				legend: legendString
			};
		} catch (e) {
			return null;
		}

	}

	createShare(legendString: string = ''): ng.IPromise<IMapShare | IMapShareData> {
		let payload = this.createSharePayload(null, legendString);

		return (new (this.getMapShareResource())(payload)).$save();
	}

	getShare(hash: string): ng.IPromise<IMapShare | IMapShareData> {
		let resource = this.readMapShareResource();
		let mapShare = resource.get({ Hash : hash });

		mapShare.$promise.catch(() => {
			this.toastService.showToast("Shared map could not be retrieved.");
		});
		
		return mapShare.$promise;
	}
}

export interface IMapShareScope extends ng.IScope {
	activeShare: IMapShare;
	activeShareURL() : string;
	copyEnabled : boolean;
}

export class MapShareCtrl implements ng.IController {
	public static $inject = [
		'$scope', 'mapShareService', 'toastService'
	];
	constructor(protected $scope : IMapShareScope, protected mapShareService : IMapShareService, protected toastService : IToastService) {
		$scope.activeShare = mapShareService.getActiveShare();
		$scope.$on('mapShareChange', (event, newValue : IMapShare/*, oldValue : IMapShare*/) => {
			if (newValue) {
				$scope.activeShare = newValue;
			}
		});

		$scope.copyEnabled = document.queryCommandSupported("copy") && (() => {
				let input = document.querySelector('input.share-link');
				return input['select'];
			})();

		$scope['copyShare'] = () => {
			toastService.showToast("Link copied", toastService.DURATION_LONG, "above-modal");

			let input = document.querySelector('input.share-link');
			if (input instanceof HTMLInputElement) {
				let val = input.value;
				input.focus();
				input.selectionStart = 0;
				input.selectionEnd = val.length;
				// input.select();
				try {
					document.execCommand('copy');
					input.blur();
				} catch (err) {
					console.warn(err);
				}
			}
		};

		$scope.activeShareURL = () => {
			if (!$scope.activeShare) return "";
			let url = window.location.host;
			let path = window.location.pathname;
			return window.location.protocol + "//" + url + (path ? path : "/") + "?" + MapShareService.PARAM + "="+ encodeURIComponent($scope.activeShare.hash);
		}
	}
}
